import { useEffect, useRef, useState } from 'react';
export function useLazyLoading(options = {}) {
    const { rootMargin = '50px', threshold = 0.1, triggerOnce = true } = options;
    const [isIntersecting, setIsIntersecting] = useState(false);
    const [hasTriggered, setHasTriggered] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        const element = ref.current;
        if (!element)
            return;
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting && (!triggerOnce || !hasTriggered)) {
                setIsIntersecting(true);
                if (triggerOnce) {
                    setHasTriggered(true);
                }
            }
            else if (!triggerOnce) {
                setIsIntersecting(entry.isIntersecting);
            }
        }, {
            rootMargin,
            threshold,
        });
        observer.observe(element);
        return () => {
            observer.disconnect();
        };
    }, [rootMargin, threshold, triggerOnce, hasTriggered]);
    return { ref, isIntersecting: triggerOnce ? hasTriggered : isIntersecting };
}
export function useLazyImage(options) {
    const { src, placeholder, debug = false, ...lazyOptions } = options;
    const [imageSrc, setImageSrc] = useState(placeholder || '');
    const [isLoaded, setIsLoaded] = useState(false);
    const [isError, setIsError] = useState(false);
    const { ref, isIntersecting } = useLazyLoading(lazyOptions);
    useEffect(() => {
        setImageSrc(placeholder || '');
        setIsLoaded(false);
        setIsError(false);
    }, [src]);
    useEffect(() => {
        if (isIntersecting && src && !isLoaded && !isError) {
            if (debug) {
                console.log('🖼️ [LazyImage] Loading image:', src);
            }
            const img = new Image();
            img.onload = () => {
                if (debug) {
                    console.log('✅ [LazyImage] Image loaded successfully:', src);
                }
                setImageSrc(src);
                setIsLoaded(true);
            };
            img.onerror = () => {
                if (debug) {
                    console.log('❌ [LazyImage] Image failed to load:', src);
                }
                setIsError(true);
            };
            img.src = src;
        }
    }, [isIntersecting, src, isLoaded, isError, debug]);
    useEffect(() => {
        if (debug && isIntersecting) {
            console.log('👀 [LazyImage] Element entered viewport:', src);
        }
    }, [isIntersecting, src, debug]);
    return {
        ref,
        src: imageSrc,
        isLoaded,
        isError,
        isIntersecting
    };
}
